import { useCallback, useEffect, useRef, useState } from 'react';

import type { BBox } from '@flightscanner/shared';

import { fetchVessels } from './apiClient';
import type { VesselsResponse } from './types';
import { enrichVessels, type RichVessel } from './vesselEnrich';
import { type ViewState, viewKey, PAN_DEBOUNCE_MS } from './useLiveStates';

export interface LiveVesselsState {
  data: VesselsResponse | null;
  vessels: RichVessel[];
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  setView: (v: ViewState) => void;
}

const POLL_MS = 10_000;

function apiErrorMessage(err: unknown): string {
  const msg = String(err);
  if (msg.includes('404')) return 'Vessels API not found';
  if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) return 'Vessels API offline';
  if (msg.includes('timed out')) return 'Vessels request timed out';
  return (err as Error).message || 'Failed to load vessels';
}

export function useLiveVessels(enabled: boolean): LiveVesselsState {
  const viewRef = useRef<ViewState | null>(null);
  const viewKeyRef = useRef('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const hasViewRef = useRef(false);
  const inFlightRef = useRef(false);
  const requestIdRef = useRef(0);
  const hasVesselsRef = useRef(false);

  const [data, setData] = useState<VesselsResponse | null>(null);
  const [vessels, setVessels] = useState<RichVessel[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyResponse = useCallback((res: VesselsResponse, key: string) => {
    if (key !== viewKeyRef.current) return;
    const next = enrichVessels(res.vessels);
    if (next.length === 0 && hasVesselsRef.current) {
      setError(null);
      return;
    }
    setData(res);
    hasVesselsRef.current = next.length > 0;
    setVessels(next);
    setError(null);
  }, []);

  const tick = useCallback(async () => {
    if (!enabled) return;
    const view = viewRef.current;
    if (!view) return;

    const reqId = ++requestIdRef.current;
    const key = viewKey(view);
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    inFlightRef.current = true;

    const hasDisplay = hasVesselsRef.current;
    if (!hasDisplay) setLoading(true);
    else setRefreshing(true);

    try {
      const res = await fetchVessels(view.bbox, ctrl.signal);
      if (reqId !== requestIdRef.current) return;
      applyResponse(res, key);
    } catch (err) {
      if (ctrl.signal.aborted) return;
      if (reqId !== requestIdRef.current) return;
      setError(apiErrorMessage(err));
    } finally {
      if (reqId === requestIdRef.current) {
        inFlightRef.current = false;
        if (abortRef.current === ctrl) abortRef.current = null;
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [applyResponse, enabled]);

  const refresh = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(tick, PAN_DEBOUNCE_MS);
  }, [tick]);

  const setView = useCallback(
    (v: ViewState) => {
      if (!enabled) return;
      const key = viewKey(v);
      const first = !hasViewRef.current;
      const moved = key !== viewKeyRef.current;
      hasViewRef.current = true;
      viewRef.current = v;
      if (moved) viewKeyRef.current = key;
      if (first) void tick();
      else if (moved) refresh();
    },
    [enabled, tick, refresh],
  );

  useEffect(() => {
    if (!enabled) {
      setVessels([]);
      setData(null);
      setLoading(false);
      setRefreshing(false);
      setError(null);
      hasVesselsRef.current = false;
      return;
    }
    const id = setInterval(() => {
      if (viewRef.current && !inFlightRef.current && !document.hidden) void tick();
    }, POLL_MS);
    const onVisible = () => {
      if (!document.hidden && viewRef.current && !inFlightRef.current) void tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, [enabled, tick]);

  return { data, vessels, loading, refreshing, error, setView };
}
